import { DriveItem } from './drive-viewer.models';
import { DriveSelectionQuery } from './drive-selection.query';
import { DriveWorkspaceState } from './drive-viewer.state';

export interface DriveContextMenuPort {
  openFolder(event: MouseEvent, folderId: string): void;
  openFile(event: MouseEvent, item: DriveItem): void;
}

export class DriveNavigationCoordinator {
  constructor(
    private readonly workspace: DriveWorkspaceState,
    private readonly selection: DriveSelectionQuery,
    private readonly contextMenu: DriveContextMenuPort,
    private readonly preview: (item: DriveItem) => void
  ) {}

  onDetailsContextMenu(event: MouseEvent): void {
    event.preventDefault();
    const selected = this.selection.selectedItem();
    if (selected && selected.type !== 'folder') {
      this.contextMenu.openFile(event, selected);
      return;
    }
    const parentId = selected?.type === 'folder'
      ? selected.id
      : selected?.parentId ? String(selected.parentId) : this.workspace.currentFolderId;
    if (!this.selection.isVirtualFolderId(parentId)) {
      this.contextMenu.openFolder(event, parentId);
    }
  }

  onContentContextMenu(event: MouseEvent): void { this.onDetailsContextMenu(event); }

  onContentRowContextMenu(event: MouseEvent, item: DriveItem): void {
    event.preventDefault();
    event.stopPropagation();
    if (item.type !== 'folder') {
      this.contextMenu.openFile(event, item);
    } else if (!this.selection.isVirtualFolderId(item.id)) {
      this.contextMenu.openFolder(event, item.id);
    }
  }

  navigateTo(id: string): void {
    this.workspace.currentFolderId = id;
    this.workspace.selectedIds.clear();
    this.expandPathTo(id);
    this.workspace.mobileTreeOpen = false;
  }

  isExpanded(id: string): boolean { return this.workspace.expanded.has(id); }

  toggleExpand(id: string, event?: MouseEvent): void {
    event?.stopPropagation();
    this.workspace.expanded.has(id)
      ? this.workspace.expanded.delete(id)
      : this.workspace.expanded.add(id);
  }

  expandPathTo(id: string): void {
    let node = this.workspace.items.find((item) => item.id === id);
    const visited = new Set<string>();
    while (node) {
      const folderId = node.type === 'folder' ? node.id : node.parentId ? String(node.parentId) : undefined;
      if (!folderId) break;
      this.workspace.expanded.add(folderId);
      if (visited.has(folderId)) break;
      visited.add(folderId);
      const parentId = this.workspace.items.find((item) => item.id === folderId)?.parentId;
      if (parentId == null) break;
      node = this.workspace.items.find((item) => item.id === String(parentId));
    }
  }

  breadcrumbClick(index: number): void {
    const targetName = this.selection.currentPath()[index];
    const folder = this.workspace.items.find((item) => item.type === 'folder'
      && item.path[index] === targetName && item.path.length === index + 1);
    if (folder) this.navigateTo(folder.id);
  }

  toggleSelect(event: MouseEvent, id: string): void {
    if (event.shiftKey || event.ctrlKey || event.metaKey) {
      this.workspace.selectedIds.has(id)
        ? this.workspace.selectedIds.delete(id)
        : this.workspace.selectedIds.add(id);
    } else {
      this.workspace.selectedIds.clear();
      this.workspace.selectedIds.add(id);
    }
  }

  toggleDetails(): void { this.workspace.detailsOpen = !this.workspace.detailsOpen; }

  onTreeItemClick(item: DriveItem, event?: MouseEvent): void {
    event?.stopPropagation();
    if (item.type === 'folder') {
      this.navigateTo(item.id);
      return;
    }
    this.workspace.selectedIds.clear();
    this.workspace.selectedIds.add(item.id);
    if (item.parentId != null) this.workspace.currentFolderId = String(item.parentId);
    this.expandPathTo(item.id);
  }

  onTreeItemDoubleClick(item: DriveItem, event: MouseEvent): void {
    event.preventDefault();
    event.stopPropagation();
    if (item.type === 'folder') {
      this.navigateTo(item.id);
      return;
    }
    this.onTreeItemClick(item);
    this.preview(item);
  }

  onItemNameClick(item: DriveItem, event: MouseEvent): void {
    if (item.type === 'folder') {
      this.navigateTo(item.id);
      event.stopPropagation();
    }
  }
}
